import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { HashRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SessionProvider, useSession } from './lib/session.js';
import { Login } from './routes/Login.js';
import { ChangePassword } from './routes/ChangePassword.js';
import { AppHome } from './routes/AppHome.js';
import { AppSettings } from './routes/AppSettings.js';
import { ApprovalInbox, PeriodList } from './routes/qaqc/PeriodList.js';
import { ReportPeriodScreen } from './routes/qaqc/ReportPeriod.js';
import { Dashboard } from './routes/qaqc/Dashboard.js';
import { DocumentGovernance } from './routes/qaqc/DocumentGovernance.js';
import { LessonForm } from './routes/lesson/LessonForm.js';
import { LessonLog } from './routes/lesson/LessonLog.js';
import { LessonApprovals } from './routes/lesson/LessonApprovals.js';
import { LessonConflicts } from './routes/lesson/LessonConflicts.js';
import { AuditSchedules } from './routes/audit/AuditSchedules.js';
import { AuditPlans } from './routes/audit/AuditPlans.js';
import { AuditList } from './routes/audit/AuditList.js';
import { AuditWorkspace } from './routes/audit/AuditWorkspace.js';
import { GuestAuditList, GuestAuditView } from './routes/audit/GuestAudits.js';
import { AuditChecklists } from './routes/audit/AuditChecklists.js';
import { FindingsLog } from './routes/audit/FindingsLog.js';
import { AuditDashboard } from './routes/audit/AuditDashboard.js';
import { AuditReport } from './routes/audit/AuditReport.js';
import { MasterData } from './routes/admin/MasterData.js';
import { MyDelegations } from './routes/admin/MyDelegations.js';
import { AccessAdmin } from './routes/admin/AccessAdmin.js';
import { AuditLog } from './routes/admin/AuditLog.js';
import { AiSettings } from './routes/admin/AiSettings.js';
import { Notifications } from './routes/Notifications.js';
import { Integrations } from './routes/admin/Integrations.js';
import { Distribution } from './routes/admin/Distribution.js';
import { DataSync } from './routes/admin/DataSync.js';
import { Reports } from './routes/Reports.js';
import { Executive } from './routes/Executive.js';
import { registerServiceWorker } from './lib/offline/register-sw.js';
import { t } from './lib/i18n.js';
import './styles/tokens.css';

/**
 * ADR-0013: HASH ROUTING, which is spec §3's primary recommendation.
 *
 * The frontend ships to S3+CloudFront under a versioned path prefix, possibly
 * inside a DronaHQ container. With hash routing, a page reload on any in-app route
 * cannot 404 whatever prefix it is served under, and there are no CloudFront
 * error-page rewrite rules to get wrong on deployment day.
 *
 * ADR-0001: three separate front doors — /qaqc, /lesson-learned, /audit — each with
 * its own home screen and its own Settings. There is deliberately NO combined
 * launchpad route; the client rejected one.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // ADR-0015: the server re-authorizes every request anyway, so a stale cache
      // cannot grant access. Refetching on focus keeps a revoked role from
      // lingering on screen after the API has already started refusing it.
      refetchOnWindowFocus: true,
      staleTime: 30_000,
      retry: 1,
    },
  },
});

function RequireSession({ children }: { children: ReactNode }) {
  const { user, loading } = useSession();
  const location = useLocation();

  if (loading) {
    return <p className="p-8 text-content-secondary">{t("Loading…")}</p>;
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  // After an administrator reset, the API refuses everything but changing the
  // password. Sending the user straight there is kinder than letting every
  // screen fail with the same 403.
  if (user.passwordChangeRequired && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return <>{children}</>;
}

function App() {
  // Everything remounts when the language changes, so every screen redraws in
  // it without subscribing (pass 12). A language change is rare and deliberate.
  const { user } = useSession();
  return (
    <Routes key={user?.locale ?? 'en'}>
      <Route path="/login" element={<Login />} />
      <Route
        path="/change-password"
        element={
          <RequireSession>
            <ChangePassword />
          </RequireSession>
        }
      />

      {/* App 1 screens. Declared BEFORE the /:app catch-alls so a two-segment
          path resolves to the screen rather than to an app home. */}
      <Route
        path="/qaqc/periods"
        element={
          <RequireSession>
            <PeriodList />
          </RequireSession>
        }
      />
      <Route
        path="/qaqc/periods/:id"
        element={
          <RequireSession>
            <ReportPeriodScreen />
          </RequireSession>
        }
      />
      <Route
        path="/qaqc/approvals"
        element={
          <RequireSession>
            <ApprovalInbox />
          </RequireSession>
        }
      />
      <Route
        path="/qaqc/dashboard"
        element={
          <RequireSession>
            <Dashboard />
          </RequireSession>
        }
      />
      <Route
        path="/qaqc/document-governance"
        element={
          <RequireSession>
            <DocumentGovernance />
          </RequireSession>
        }
      />

      {/* App 2 screens. Same ordering rule as App 1: two-segment paths first,
          so /lesson-learned/log is the log and not an app home. */}
      <Route
        path="/lesson-learned/log"
        element={
          <RequireSession>
            <LessonLog />
          </RequireSession>
        }
      />
      <Route
        path="/lesson-learned/new"
        element={
          <RequireSession>
            <LessonForm />
          </RequireSession>
        }
      />
      <Route
        path="/lesson-learned/lessons/:id"
        element={
          <RequireSession>
            <LessonForm />
          </RequireSession>
        }
      />
      <Route
        path="/lesson-learned/approvals"
        element={
          <RequireSession>
            <LessonApprovals />
          </RequireSession>
        }
      />
      <Route
        path="/lesson-learned/conflicts"
        element={
          <RequireSession>
            <LessonConflicts />
          </RequireSession>
        }
      />


      {/* App 3 screens (spec §11). Same ordering rule: two-segment paths first. */}
      <Route
        path="/audit/schedules"
        element={
          <RequireSession>
            <AuditSchedules />
          </RequireSession>
        }
      />
      <Route
        path="/audit/plans"
        element={
          <RequireSession>
            <AuditPlans />
          </RequireSession>
        }
      />
      <Route
        path="/audit/checklists"
        element={
          <RequireSession>
            <AuditChecklists />
          </RequireSession>
        }
      />
      <Route
        path="/audit/invited"
        element={
          <RequireSession>
            <GuestAuditList />
          </RequireSession>
        }
      />
      <Route
        path="/audit/invited/:id"
        element={
          <RequireSession>
            <GuestAuditView />
          </RequireSession>
        }
      />
      <Route
        path="/audit/audits"
        element={
          <RequireSession>
            <AuditList />
          </RequireSession>
        }
      />
      <Route
        path="/audit/audits/:id"
        element={
          <RequireSession>
            <AuditWorkspace />
          </RequireSession>
        }
      />
      <Route
        path="/audit/audits/:id/report"
        element={
          <RequireSession>
            <AuditReport />
          </RequireSession>
        }
      />
      <Route
        path="/audit/findings"
        element={
          <RequireSession>
            <FindingsLog />
          </RequireSession>
        }
      />
      <Route
        path="/audit/dashboard"
        element={
          <RequireSession>
            <AuditDashboard />
          </RequireSession>
        }
      />
      {/* Executive 360 (spec §13.8): a view across the three, reached from each
          app's switcher. Declared before "/:app" so it is not read as an app. */}
      <Route
        path="/executive"
        element={
          <RequireSession>
            <Executive />
          </RequireSession>
        }
      />
      {/* Three front doors. Same design system, separate applications. */}
      <Route
        path="/:app"
        element={
          <RequireSession>
            <AppHome />
          </RequireSession>
        }
      />
      {/* Shared Admin Core (spec §13.5), reached from each app's own Settings.
          Built once, exposed per app — ADR-0001, no shared launchpad. */}
      <Route
        path="/:app/delegations"
        element={
          <RequireSession>
            <MyDelegations />
          </RequireSession>
        }
      />
      <Route
        path="/:app/settings/access"
        element={
          <RequireSession>
            <AccessAdmin />
          </RequireSession>
        }
      />
      <Route
        path="/:app/settings/master-data"
        element={
          <RequireSession>
            <MasterData />
          </RequireSession>
        }
      />
      <Route
        path="/:app/settings/audit-log"
        element={
          <RequireSession>
            <AuditLog />
          </RequireSession>
        }
      />
      <Route
        path="/:app/settings/ai"
        element={
          <RequireSession>
            <AiSettings />
          </RequireSession>
        }
      />
      <Route
        path="/:app/settings/distribution"
        element={
          <RequireSession>
            <Distribution />
          </RequireSession>
        }
      />
      <Route
        path="/:app/settings/data-sync"
        element={
          <RequireSession>
            <DataSync />
          </RequireSession>
        }
      />
      <Route
        path="/:app/settings/integrations"
        element={
          <RequireSession>
            <Integrations />
          </RequireSession>
        }
      />
      {/* The Reports Hub (spec §13.8), per application. */}
      <Route
        path="/:app/reports"
        element={
          <RequireSession>
            <Reports />
          </RequireSession>
        }
      />
      {/* The Notification Center (spec §13.6): one inbox, reached from every app's bar. */}
      <Route
        path="/:app/notifications"
        element={
          <RequireSession>
            <Notifications />
          </RequireSession>
        }
      />
      <Route
        path="/:app/settings"
        element={
          <RequireSession>
            <AppSettings />
          </RequireSession>
        }
      />

      {/* No landing page: the root sends you to an app, not to a hub. */}
      <Route path="/" element={<Navigate to="/qaqc" replace />} />
      <Route path="*" element={<Navigate to="/qaqc" replace />} />
    </Routes>
  );
}

/**
 * The offline shell (spec §10).
 *
 * Registered for the whole build rather than only App 2: the worker caches the
 * application shell, and the shell is shared. Only App 2 uses the outbox.
 */
registerServiceWorker();

const container = document.getElementById('root');
if (!container) throw new Error(t("Root element missing from index.html."));

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Opt in to the v7 behaviours now, so the upgrade is not a surprise later
          and the console stays free of noise that hides real warnings. */}
      <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <SessionProvider>
          <App />
        </SessionProvider>
      </HashRouter>
    </QueryClientProvider>
  </StrictMode>,
);
